import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, IndianRupee, Clock, Shield, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONFIG } from '../config';

// Spark particle component
const SparkParticle = ({ delay, duration, x, y }) => (
  <motion.div
    className="absolute w-1 h-1 rounded-full bg-[#5C1F33]"
    style={{ left: x, top: y }}
    animate={{
      opacity: [0, 1, 0],
      scale: [0, 1.5, 0],
      x: [0, Math.random() * 40 - 20],
      y: [0, Math.random() * -60],
    }}
    transition={{
      duration: duration || 2,
      delay: delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 3,
    }}
  />
);

// Floating spark container
const SparkField = () => {
  const sparks = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: `${20 + Math.random() * 60}%`,
    y: `${20 + Math.random() * 60}%`,
    delay: Math.random() * 2,
    duration: 1.5 + Math.random() * 1.5,
  }));

  return (
    <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
      {sparks.map((spark) => (
        <SparkParticle key={spark.id} {...spark} />
      ))}
    </div>
  );
};

const HeroExact = () => {
  return (
    <section className="relative pt-12 pb-8 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-white to-gray-50 border border-gray-200/80 rounded-full px-5 py-2.5 mt-8 mb-8 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5C1F33] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#5C1F33]"></span>
          </span>
          <span className="text-sm text-[#333333] font-medium">Trusted for fast, transparent business funding</span>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Right Visual - Animated */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:flex order-2 relative items-center justify-center -mt-28"
          >
            <div className="relative w-[680px] h-[680px]">
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="absolute w-[520px] h-[520px] rounded-full border border-[#5C1F33]/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                >
                  {[0, 72, 144, 216, 288].map((angle, i) => (
                    <motion.div
                      key={`outer-dot-${i}`}
                      className="absolute w-4 h-4 rounded-full bg-[#5C1F33]"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${angle}deg) translateX(260px) translateY(-50%)`,
                      }}
                    />
                  ))}
                </motion.div>

                <motion.div
                  className="absolute w-[390px] h-[390px] rounded-full border border-[#5C1F33]/20"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                >
                  {[0, 90, 180, 270].map((angle, i) => (
                    <motion.div
                      key={`mid-dot-${i}`}
                      className="absolute w-3 h-3 rounded-full bg-[#800020]"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${angle}deg) translateX(195px) translateY(-50%)`,
                      }}
                    />
                  ))}
                </motion.div>

                <motion.div
                  className="absolute w-[260px] h-[260px] rounded-full border border-[#5C1F33]/25"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  {[0, 120, 240].map((angle, i) => (
                    <motion.div
                      key={`inner-dot-${i}`}
                      className="absolute w-3 h-3 rounded-full bg-[#5C1F33]/70"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${angle}deg) translateX(130px) translateY(-50%)`,
                      }}
                    />
                  ))}
                </motion.div>
              </div>

              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="relative w-[285px] h-[285px] rounded-full"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 40%, rgba(92,31,51,0.1) 100%)',
                    boxShadow: 'inset -10px -10px 30px rgba(92,31,51,0.2), 0 10px 40px rgba(92,31,51,0.3), 0 0 60px rgba(128,0,32,0.2)',
                    border: '1px solid rgba(255,255,255,0.5)',
                  }}
                  animate={{ y: [0, -10, 0], scale: [1, 1.02, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center mt-3">
                      <img
                        src="/logo.69793bbbefb277f660f7-removebg-preview.png"
                        alt="Funding Sathi Logo"
                        className="max-w-[100px] h-auto object-contain -mt-6"
                      />
                      <img
                        src="/treadmark.png"
                        alt="Trade Mark"
                        className="max-w-[180px] h-auto object-contain mt-1"
                      />
                  </div>
                </motion.div>
              </div>

              {/* Alternative background network pattern */}
              <motion.svg
                viewBox="0 0 600 600"
                className="absolute inset-0 z-10 pointer-events-none"
                style={{ transform: "scale(1.15)", transformOrigin: "50% 50%" }}
                initial={{ opacity: 0.35 }}
                animate={{ opacity: [0.22, 0.45, 0.22] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              >
                {[
                  [124,160,210,114],[210,114,302,98],[302,98,394,112],[394,112,468,170],
                  [468,170,494,252],[494,252,486,338],[486,338,444,416],
                  [444,416,372,470],[372,470,286,494],[286,494,204,470],[204,470,146,418],[146,418,116,342],
                  [116,342,110,262],[110,262,124,160],
                  [170,204,248,168],[248,168,334,168],[334,168,402,210],
                  [402,210,416,286],[416,286,394,356],[394,356,334,402],
                  [334,402,260,418],[260,418,204,386],[204,386,170,324],[170,324,166,258],[166,258,170,204],
                  [248,168,260,246],[334,168,318,250],[402,210,356,278],
                  [204,386,260,418],[170,324,204,386],
                  [124,160,170,204],[210,114,248,168],[302,98,334,168],[394,112,402,210],[468,170,402,210],
                  [494,252,416,286],[486,338,394,356],[444,416,334,402],[372,470,334,402],[286,494,260,418],
                  [204,470,204,386],[146,418,170,324],[116,342,170,324],[110,262,166,258],
                  [170,204,260,246],[260,246,318,250],[318,250,356,278],[356,278,394,356],
                  [248,168,318,250],[334,168,356,278],[260,418,334,402]
                ].map((line, i) => (
                  <line
                    key={`mesh-line-${i}`}
                    x1={line[0]}
                    y1={line[1]}
                    x2={line[2]}
                    y2={line[3]}
                    stroke="rgba(92,31,51,0.14)"
                    strokeWidth="1"
                  />
                ))}
                {[
                  [124,160],[210,114],[302,98],[394,112],[468,170],[494,252],[486,338],[444,416],[372,470],[286,494],[204,470],[146,418],[116,342],[110,262],
                  [170,204],[248,168],[334,168],[402,210],[416,286],[394,356],[334,402],[260,418],[204,386],[170,324],[166,258],
                  [146,210],[452,126],[478,406],[132,454],[300,78],[318,454]
                ].map((node, i) => (
                  <motion.circle
                    key={`mesh-node-${i}`}
                    cx={node[0]}
                    cy={node[1]}
                    r={i % 4 === 0 ? 5 : 4}
                    fill={i % 6 === 0 ? "#6f1531" : "#b16782"}
                    animate={{ opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 2.8, repeat: Infinity, delay: (i % 8) * 0.16 }}
                  />
                ))}
              </motion.svg>

              <SparkField />
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-1"
          >
            {/* Headline */}
            <motion.h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#333333] mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="text-[#5C1F33] text-4xl sm:text-5xl lg:text-6xl">Turn Your Verified Invoices</span>
              <br />
              <span className="text-[#333333] text-2xl sm:text-3xl lg:text-4xl">Into Working Capital Instantly.</span>
            </motion.h1>
            
            {/* Description */}
            <motion.p 
              className="text-[#666666] mb-8 leading-relaxed max-w-lg text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Secure the working capital your business needs with streamlined approvals, light documentation and clear, dependable financing designed for verified trade.
            </motion.p>

            {/* Trust Badges */}
            <motion.div 
              className="flex flex-wrap gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <motion.div 
                className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200/50 hover:border-[#5C1F33]/30 transition-colors cursor-default"
                whileHover={{ y: -2 }}
              >
                <div className="w-8 h-8 bg-[#5C1F33]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-[#5C1F33]" />
                </div>
          <span className="text-sm text-[#666666] font-medium">Fast & Transparent Process</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-200/50 hover:border-[#5C1F33]/30 transition-colors cursor-default"
                whileHover={{ y: -2 }}
              >
                <div className="w-8 h-8 bg-[#5C1F33]/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-[#5C1F33]" />
                </div>
                <span className="text-sm text-[#666666] font-medium">Minimal Documentation</span>
              </motion.div>
            </motion.div>
          </motion.div>

        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-14 rounded-3xl bg-[#f7f5f3] border border-gray-200 p-6 sm:p-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#800020]">Why businesses choose us</p>
              <p className="mt-2 text-sm text-[#555555] max-w-xl">A compact set of metrics that highlights our speed, scale and approval strength.</p>
            </div>
            <motion.div
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-[#333333] shadow-sm"
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <span className="h-2 w-2 rounded-full bg-[#5C1F33] animate-pulse"></span>
              Live Funding Pulse
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.div
              className="rounded-3xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              whileHover={{ y: -3 }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.55 }}
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[#5C1F33]/10 text-[#5C1F33] mb-4">
                <Clock className="w-5 h-5" />
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#222222]">Fast Funds</p>
              <p className="mt-2 text-xs sm:text-sm text-[#666666]">Invoice converted to funds in just one business day, after standard verification.</p>
            </motion.div>

            <motion.div
              className="rounded-3xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              whileHover={{ y: -3 }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.65 }}
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[#5C1F33]/10 text-[#5C1F33] mb-4">
                <IndianRupee className="w-5 h-5" />
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#222222]">₹5,000 M+</p>
              <p className="mt-2 text-xs sm:text-sm text-[#666666]">Proposal Enabled</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroExact;
